import {
  BookOpen,
  LayoutGrid,
  Lightbulb,
  Menu,
  Package,
  Percent,
  Search,
  Shield,
  ShoppingCart,
  Store,
  User,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useInitiativeDialog } from "@/features/tickets";
import { ROUTES } from "@/shared/model/routes";
import { Button } from "@/shared/ui/kit/button";
import { Separator } from "@/shared/ui/kit/separator";
import { UnifiedDrawer } from "@/shared/ui/unified-drawer";

import type { UseNavigationModel } from "../model";

type NavLink = {
  label: string;
  route: string;
  icon: LucideIcon;
  description: string;
};

type NavDrawerProps = {
  model: UseNavigationModel;
  onSearchOpen: () => void;
};

export function NavDrawer({ model, onSearchOpen }: NavDrawerProps) {
  const { t } = useTranslation();
  const initiativeDialog = useInitiativeDialog();

  const buyersLinks: NavLink[] = [
    {
      label: t("nav.howToBuy"),
      route: ROUTES.USER_HOW_TO_BUY,
      icon: ShoppingCart,
      description: t("nav.descriptions.howToBuy"),
    },
    {
      label: t("nav.guarantees"),
      route: ROUTES.USER_GUARANTEES,
      icon: Shield,
      description: t("nav.descriptions.guarantees"),
    },
    {
      label: t("nav.rules"),
      route: ROUTES.USER_RULES,
      icon: BookOpen,
      description: t("nav.descriptions.rules"),
    },
  ];

  const sellersLinks: NavLink[] = [
    {
      label: t("nav.becomeSeller"),
      route: ROUTES.STORE_BECOME_STORE,
      icon: Store,
      description: t("nav.descriptions.becomeSeller"),
    },
    {
      label: t("nav.howToSell"),
      route: ROUTES.STORE_HOW_TO_SELL,
      icon: Package,
      description: t("nav.descriptions.howToSell"),
    },
    {
      label: t("nav.guarantees"),
      route: ROUTES.STORE_GUARANTEES,
      icon: Shield,
      description: t("nav.descriptions.storeGuarantees"),
    },
    {
      label: t("nav.rules"),
      route: ROUTES.STORE_RULES,
      icon: BookOpen,
      description: t("nav.descriptions.storeRules"),
    },
    {
      label: t("nav.commission"),
      route: ROUTES.STORE_COMMISSION,
      icon: Percent,
      description: t("nav.descriptions.commission"),
    },
  ];

  const handleClose = () => {
    model.setIsMobileMenuOpen(false);
  };

  return (
    <UnifiedDrawer
      open={model.isMobileMenuOpen}
      onOpenChange={model.setIsMobileMenuOpen}
      direction="right"
    >
      <UnifiedDrawer.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("nav.openMenu")}>
          <Menu className="w-5 h-5" />
        </Button>
      </UnifiedDrawer.Trigger>
      <UnifiedDrawer.Content className="w-[85vw] max-w-sm h-full flex flex-col">
        {/* Search */}
        <UnifiedDrawer.Header className="px-4 py-3">
          <Button
            variant="outline"
            className="justify-start gap-2 w-full h-8"
            onClick={() => {
              onSearchOpen();
              handleClose();
            }}
          >
            <Search className="w-4 h-4" />
            <span className="text-muted-foreground">
              {t("search.searchPlaceholder")}
            </span>
          </Button>
        </UnifiedDrawer.Header>

        {/* Catalog */}
        <UnifiedDrawer.Header className="px-4 py-3 border-t">
          <Button
            variant="outline"
            className="justify-start gap-2 w-full h-8"
            asChild
            onClick={handleClose}
          >
            <Link to={ROUTES.PRODUCTS}>
              <LayoutGrid className="w-4 h-4" />
              {t("nav.catalog")}
            </Link>
          </Button>
        </UnifiedDrawer.Header>

        {/* Suggest Idea button - Only for authenticated users */}
        {model.isAuthenticated && (
          <UnifiedDrawer.Header className="px-4 py-3 border-t">
            <Button
              variant="outline"
              className="justify-start gap-2 w-full h-8"
              onClick={() => {
                initiativeDialog.open();
                handleClose();
              }}
            >
              <Lightbulb className="w-4 h-4 text-amber-500" />
              {t("nav.suggestIdea")}
            </Button>
          </UnifiedDrawer.Header>
        )}

        {/* Scrollable Content */}
        <UnifiedDrawer.Body className="px-4 py-4 flex-1 overflow-y-auto">
          {/* Buyers section */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground px-2">
              {t("nav.buyers")}
            </h3>
            <nav className="flex flex-col gap-1">
              {buyersLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.route}
                    to={link.route}
                    onClick={handleClose}
                    className="flex items-start gap-3 px-2 py-2 rounded-md hover:bg-accent"
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium">{link.label}</span>
                      <span className="text-xs text-muted-foreground leading-tight">
                        {link.description}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>

          <Separator className="my-4" />

          {/* Sellers section */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground px-2">
              {t("nav.forStore")}
            </h3>
            <nav className="flex flex-col gap-1">
              {sellersLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.route}
                    to={link.route}
                    onClick={handleClose}
                    className="flex items-start gap-3 px-2 py-2 rounded-md hover:bg-accent"
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium">{link.label}</span>
                      <span className="text-xs text-muted-foreground leading-tight">
                        {link.description}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>
        </UnifiedDrawer.Body>

        {/* Fixed Footer - Auth buttons for non-authenticated users */}
        {!model.isAuthenticated && (
          <UnifiedDrawer.Footer className="p-4 pt-0 border-t">
            <div className="flex flex-col gap-2">
              <Button variant="ghost" asChild onClick={handleClose}>
                <Link to={ROUTES.LOGIN}>
                  <User className="w-4 h-4 mr-2" />
                  {t("nav.login")}
                </Link>
              </Button>
              <Button asChild onClick={handleClose}>
                <Link to={ROUTES.REGISTER}>{t("nav.register")}</Link>
              </Button>
            </div>
          </UnifiedDrawer.Footer>
        )}
      </UnifiedDrawer.Content>
    </UnifiedDrawer>
  );
}
